import { Routes } from '@angular/router';
import { authGuard } from './shared/guards/auth.guard';
import { inviteStudentDeactivateGuard } from './shared/guards/invite-student-deactivate.guard';
import { AdminGuard } from './shared/guards/admin.guard';
import { PublicGuard } from './shared/guards/public.guard';
import { subjectLicenseGuard } from './shared/guards/subject-license.guard';
import { subjectOwnerGuard } from './shared/guards/subject-owner.guard';
import { groupOwnerGuard } from './shared/guards/group-owner.guard';
import { classworkSessionSubjectGuard } from './shared/guards/classwork-session-subject.guard';
import { environment } from '../environments/environment';

export function loadSubjectDashboard() {
  return environment.features.teacherRedesign
    ? import('./pages/subjects/subject-overview/subject-overview.page').then(m => m.SubjectOverviewPage)
    : import('./pages/subjects/subject-dashboard/subject-dashboard.page').then(m => m.SubjectDashboardPage);
}

export const routes: Routes = [
  { path: '', redirectTo: 'select-subject', pathMatch: 'full' },
  {
    path: 'password-reset/:id',
    loadComponent: () => import('./pages/login/password-reset/password-reset.page').then(m => m.PasswordResetPage),
    canActivate: [PublicGuard]
  },
  {
    path: 'forgot-pass',
    loadComponent: () => import('./pages/login/forgot-pass/forgot-pass.page').then(m => m.ForgotPassPage),
    canActivate: [PublicGuard]
  },
  {
    path: 'email-confirmation/:token',
    loadComponent: () => import('./pages/login/email-confirmation/email-confirmation.page').then(m => m.EmailConfirmationPage),
    canActivate: [PublicGuard]
  },
  {
    path: 'login',
    loadComponent: () => import('./pages/login/login.page').then(m => m.LoginPage),
    canActivate: [PublicGuard]
  },
  {
    path: 'registration',
    loadComponent: () => import('./pages/registration/registration.page').then(m => m.RegistrationPage),
    canActivate: [PublicGuard]
  },
  {
    path: 'profile-delete-help',
    loadComponent: () => import('./pages/profile-delete-help/profile-delete-help.page').then(m => m.ProfileDeleteHelpPage),
    canActivate: [PublicGuard]
  },
  {
    path: 'select-subject',
    loadComponent: () => import('./pages/select-subject/select-subject.page').then(m => m.SelectSubjectPage),
    canActivate: [authGuard]
  },
  {
    path: 'add-content',
    loadComponent: () => import('./pages/add-content/add-content.page').then(m => m.AddContentPage),
    canActivate: [authGuard],
  },
  {
    path: 'add-content/generations',
    loadComponent: () => import('./pages/generations/generations.page').then(m => m.GenerationsPage),
    canActivate: [authGuard]
  },
  {
    path: 'add-content/generations/create',
    loadComponent: () => import('./pages/create-generation/create-generation.page').then(m => m.CreateGenerationPage),
    canActivate: [authGuard]
  },
  {
    path: 'add-content/generations/:generationProcessId',
    loadComponent: () => import('./pages/generation-process/generation-process.page').then(m => m.GenerationProcessPage),
    canActivate: [authGuard],
    children: [
      {
        path: '',
        redirectTo: 'toc',
        pathMatch: 'full'
      },
      {
        path: 'toc',
        loadComponent: () => import('./pages/generation-process/toc/toc.component').then(m => m.TocComponent)
      },
      {
        path: 'knowledge-elements',
        loadComponent: () => import('./pages/generation-process/knowledge-elements-tab/knowledge-elements-tab.component').then(m => m.KnowledgeElementsTabComponent)
      },
      {
        path: 'recogs',
        loadComponent: () => import('./pages/generation-process/recogs-tab/recogs-tab.component').then(m => m.RecogsTabComponent)
      }
    ]
  },
  {
    path: 'marketplace',
    loadComponent: () => import('./pages/marketplace/marketplace.page').then(m => m.MarketplacePage),
    canActivate: [authGuard]
  },
  {
    path: 'marketplace/subject/:subjectId',
    loadComponent: () => import('./pages/marketplace-subject/marketplace-subject.page').then(m => m.MarketplaceSubjectPage),
    canActivate: [authGuard]
  },
  {
    path: 'profile',
    loadComponent: () => import('./pages/profile/profile.page').then(m => m.ProfilePage),
    canActivate: [authGuard]
  },
  {
    path: 'users',
    loadComponent: () => import('./pages/users/users.page').then(m => m.UsersPage),
    canActivate: [AdminGuard]
  },
  {
    path: 'users/add-to-group',
    loadComponent: () => import('./pages/users/add-to-group/add-to-group.page').then(m => m.AddToGroupPage),
    canActivate: [AdminGuard]
  },
  {
    path: 'subjects/:subjectId/teaching/classwork-session/:classworkSessionId',
    loadComponent: () => import('./pages/classwork-session/classwork-session.page').then(m => m.ClassworkSessionPage),
    canActivate: [authGuard, subjectOwnerGuard, classworkSessionSubjectGuard, subjectLicenseGuard]
  },
  {
    path: 'create-homework',
    loadComponent: () => import('./pages/subjects/subject-teaching/homeworks/create-homework/create-homework.page').then(m => m.CreateHomeworkPage)
  },
  {
    path: 'subjects',
    loadComponent: () => import('./shared/components/subject-layout/subject-layout.component').then(m => m.SubjectLayoutComponent),
    canActivate: [authGuard],
    children: [
      {
        path: ':subjectId',
        canActivate: [subjectOwnerGuard],
        children: [
          {
            path: 'dashboard',
            loadComponent: loadSubjectDashboard,
            canActivate: [authGuard]
          },
          {
            path: 'profile',
            loadComponent: () => import('./pages/profile/profile.page').then(m => m.ProfilePage),
            canActivate: [authGuard]
          },
          {
            path: 'help',
            loadComponent: () => import('./pages/help/help.page').then(m => m.HelpPage),
            canActivate: [authGuard]
          },
          {
            path: 'teaching/groups/:groupId/students',
            loadComponent: () => import('./pages/subjects/subject-teaching/students/students.page').then(m => m.StudentsPage),
            canActivate: [authGuard, groupOwnerGuard],
            data: { studentsRoute: true },
            children: [
              {
                path: 'invite',
                loadComponent: () => import('./pages/subjects/subject-teaching/students/invite-student/invite-student.page').then(m => m.InviteStudentPage),
                canActivate: [authGuard, groupOwnerGuard],
                canDeactivate: [inviteStudentDeactivateGuard],
                data: { studentsRoute: true, routeModal: true }
              }
            ]
          },
          {
            path: 'teaching/groups',
            loadComponent: () => import('./pages/subjects/subject-teaching/groups/groups-list/groups-list.page').then(m => m.GroupsListPage),
            canActivate: [authGuard],
            children: [
              {
                path: 'create',
                loadComponent: () => import('./pages/subjects/subject-teaching/groups/groups-form/groups-form.page').then(m => m.GroupsFormPage),
                canActivate: [authGuard]
              },
              {
                path: ':groupId',
                loadComponent: () => import('./pages/subjects/subject-teaching/groups/groups-form/groups-form.page').then(m => m.GroupsFormPage),
                canActivate: [authGuard, groupOwnerGuard]
              }
            ]
          },
          {
            path: 'teaching',
            loadComponent: () => import('./pages/subjects/subject-teaching/subject-teaching.page').then(m => m.SubjectTeachingPage),
            canActivate: [/*subjectTeachingGuard, teachingAllGuard, */authGuard],
            children: [
              {
                path: 'no-groups',
                loadComponent: () => import('./pages/subjects/subject-teaching/no-groups/no-groups.page').then(m => m.NoGroupsPage)
              },
              {
                path: 'all',
                children: [
                  {
                    path: 'statistics',
                    loadComponent: () => import('./pages/statistics/subject-statistics/subject-statistics.page').then(m => m.SubjectStatisticsPage)
                  },
                  {
                    path: 'classworks',
                    loadComponent: () => import('./pages/subjects/subject-teaching/classworks/classworks.page').then(m => m.ClassworksPage),
                    canActivate: [subjectLicenseGuard]
                  },
                  {
                    path: 'classworks/create/new-package',
                    loadComponent: () => import('./pages/package/details/package-details.page').then(m => m.PackageDetailsPage),
                    canActivate: [subjectLicenseGuard]
                  },
                  {
                    path: 'classworks/statistics',
                    loadComponent: () => import('./pages/statistics/classwork-statistics/classwork-statistics.page').then(m => m.ClassworkStatisticsPage),
                    canActivate: [subjectLicenseGuard]
                  },
                  {
                    path: 'classworks/:learningTaskConfigurationId/statistics',
                    loadComponent: () => import('./pages/statistics/classwork-statistics/classwork-statistics.page').then(m => m.ClassworkStatisticsPage),
                    canActivate: [subjectLicenseGuard]
                  },
                  {
                    path: 'homeworks',
                    loadComponent: () => import('./pages/subjects/subject-teaching/homeworks/homeworks.page').then(m => m.HomeworksPage),
                    canActivate: [subjectLicenseGuard]
                  },
                  {
                    path: 'homeworks/statistics',
                    loadComponent: () => import('./pages/statistics/homework-statistics/homework-statistics.page').then(m => m.HomeworkStatisticsPage),
                    canActivate: [subjectLicenseGuard]
                  },
                  {
                    path: 'homeworks/create',
                    loadComponent: () => import('./pages/subjects/subject-teaching/homeworks/create-homework/create-homework.page').then(m => m.CreateHomeworkPage),
                    canActivate: [subjectLicenseGuard]
                  },
                  {
                    path: 'homeworks/create/new-package',
                    loadComponent: () => import('./pages/package/details/package-details.page').then(m => m.PackageDetailsPage),
                    canActivate: [subjectLicenseGuard]
                  },
                  {
                    path: 'homeworks/:learningTaskConfigurationId',
                    loadComponent: () => import('./pages/subjects/subject-teaching/homeworks/create-homework/create-homework.page').then(m => m.CreateHomeworkPage),
                    canActivate: [subjectLicenseGuard]
                  },
                  {
                    path: 'homeworks/:learningTaskConfigurationId/new-package',
                    loadComponent: () => import('./pages/package/details/package-details.page').then(m => m.PackageDetailsPage),
                    canActivate: [subjectLicenseGuard]
                  },
                  {
                    path: 'homeworks/:learningTaskConfigurationId/statistics',
                    loadComponent: () => import('./pages/statistics/homework-statistics/homework-statistics.page').then(m => m.HomeworkStatisticsPage),
                    canActivate: [subjectLicenseGuard]
                  },
                  {
                    path: 'exams',
                    loadComponent: () => import('./pages/subjects/subject-teaching/exams/exams.page').then(m => m.ExamsPage),
                    canActivate: [subjectLicenseGuard]
                  },
                  {
                    path: 'exams/statistics',
                    loadComponent: () => import('./pages/statistics/exam-statistics/exam-statistics.page').then(m => m.ExamStatisticsPage),
                    canActivate: [subjectLicenseGuard]
                  },
                  {
                    path: 'exams/create',
                    loadComponent: () => import('./pages/subjects/subject-teaching/exams/create-exam/create-exam.page').then(m => m.CreateExamPage),
                    canActivate: [subjectLicenseGuard]
                  },
                  {
                    path: 'exams/create/new-package',
                    loadComponent: () => import('./pages/package/details/package-details.page').then(m => m.PackageDetailsPage),
                    canActivate: [subjectLicenseGuard]
                  },
                  {
                    path: 'exams/:learningTaskConfigurationId/statistics',
                    loadComponent: () => import('./pages/statistics/exam-statistics/exam-statistics.page').then(m => m.ExamStatisticsPage),
                    canActivate: [subjectLicenseGuard]
                  },
                  {
                    path: 'exams/:learningTaskConfigurationId',
                    loadComponent: () => import('./pages/subjects/subject-teaching/exams/create-exam/create-exam.page').then(m => m.CreateExamPage),
                    canActivate: [subjectLicenseGuard]
                  },
                  {
                    path: 'exams/:learningTaskConfigurationId/new-package',
                    loadComponent: () => import('./pages/package/details/package-details.page').then(m => m.PackageDetailsPage),
                    canActivate: [subjectLicenseGuard]
                  },
                  {
                    path: 'homelearning',
                    loadComponent: () => import('./pages/subjects/subject-teaching/homelearning/configuration/configuration.page').then(m => m.ConfigurationPage),
                    canActivate: [subjectLicenseGuard]
                  },
                  {
                    path: 'homelearning/students',
                    loadComponent: () => import('./pages/subjects/subject-teaching/homelearning/configuration/configuration.page').then(m => m.ConfigurationPage),
                    data: { studentList: true },
                    canActivate: [subjectLicenseGuard]
                  },
                  {
                    path: 'homelearning/configuration',
                    loadComponent: () => import('./pages/subjects/subject-teaching/homelearning/configuration/configuration.page').then(m => m.ConfigurationPage),
                    canActivate: [subjectLicenseGuard]
                  },
                  {
                    path: 'homelearning/configuration/:studentId',
                    loadComponent: () => import('./pages/subjects/subject-teaching/homelearning/configuration/configuration.page').then(m => m.ConfigurationPage),
                    canActivate: [subjectLicenseGuard]
                  },
                  {
                    path: 'homelearning/statistics',
                    loadComponent: () => import('./pages/statistics/homelearning-statistics/homelearning-statistics.page').then(m => m.HomelearningStatisticsPage),
                    canActivate: [subjectLicenseGuard]
                  },
                  {
                    path: 'homelearning/statistics/:userId',
                    loadComponent: () => import('./pages/statistics/homelearning-statistics/homelearning-statistics.page').then(m => m.HomelearningStatisticsPage),
                    canActivate: [subjectLicenseGuard]
                  },
                  {
                    path: 'students',
                    loadComponent: () => import('./pages/subjects/subject-teaching/students/students.page').then(m => m.StudentsPage)
                  },
                  {
                    path: 'tasks',
                    loadComponent: () => import('./pages/subjects/subject-teaching/tasks/tasks.page').then(m => m.TasksPage)
                  },
                  {
                    path: 'invite',
                    redirectTo: 'students',
                    pathMatch: 'full'
                  },
                  {
                    path: '',
                    redirectTo: 'classworks',
                    pathMatch: 'full'
                  }
                ]
              },
              {
                path: ':groupId',
                canActivate: [groupOwnerGuard],
                children: [
                  {
                    path: 'classworks',
                    loadComponent: () => import('./pages/subjects/subject-teaching/classworks/classworks.page').then(m => m.ClassworksPage),
                    canActivate: [subjectLicenseGuard]
                  },
                  {
                    path: 'classworks/statistics',
                    loadComponent: () => import('./pages/statistics/classwork-statistics/classwork-statistics.page').then(m => m.ClassworkStatisticsPage),
                    canActivate: [subjectLicenseGuard]
                  },
                  {
                    path: 'classworks/:learningTaskConfigurationId/statistics',
                    loadComponent: () => import('./pages/statistics/classwork-statistics/classwork-statistics.page').then(m => m.ClassworkStatisticsPage),
                    canActivate: [subjectLicenseGuard]
                  },
                  {
                    path: 'classworks/create',
                    loadComponent: () => import('./pages/subjects/subject-teaching/classworks/create-classwork/create-classwork.page').then(m => m.CreateClassworkPage),
                    canActivate: [subjectLicenseGuard]
                  },
                  {
                    path: 'classworks/create/new-package',
                    loadComponent: () => import('./pages/package/details/package-details.page').then(m => m.PackageDetailsPage),
                    canActivate: [subjectLicenseGuard]
                  },
                  {
                    path: 'homeworks',
                    loadComponent: () => import('./pages/subjects/subject-teaching/homeworks/homeworks.page').then(m => m.HomeworksPage),
                    canActivate: [subjectLicenseGuard]
                  },
                  {
                    path: 'homeworks/statistics',
                    loadComponent: () => import('./pages/statistics/homework-statistics/homework-statistics.page').then(m => m.HomeworkStatisticsPage),
                    canActivate: [subjectLicenseGuard]
                  },
                  {
                    path: 'homeworks/create',
                    loadComponent: () => import('./pages/subjects/subject-teaching/homeworks/create-homework/create-homework.page').then(m => m.CreateHomeworkPage),
                    canActivate: [subjectLicenseGuard]
                  },
                  {
                    path: 'homeworks/create/new-package',
                    loadComponent: () => import('./pages/package/details/package-details.page').then(m => m.PackageDetailsPage),
                    canActivate: [subjectLicenseGuard]
                  },
                  {
                    path: 'homeworks/:learningTaskConfigurationId',
                    loadComponent: () => import('./pages/subjects/subject-teaching/homeworks/create-homework/create-homework.page').then(m => m.CreateHomeworkPage),
                    canActivate: [subjectLicenseGuard]
                  },
                  {
                    path: 'homeworks/:learningTaskConfigurationId/new-package',
                    loadComponent: () => import('./pages/package/details/package-details.page').then(m => m.PackageDetailsPage),
                    canActivate: [subjectLicenseGuard]
                  },
                  {
                    path: 'homeworks/:learningTaskConfigurationId/statistics',
                    loadComponent: () => import('./pages/statistics/homework-statistics/homework-statistics.page').then(m => m.HomeworkStatisticsPage),
                    canActivate: [subjectLicenseGuard]
                  },
                  {
                    path: 'exams',
                    loadComponent: () => import('./pages/subjects/subject-teaching/exams/exams.page').then(m => m.ExamsPage),
                    canActivate: [subjectLicenseGuard]
                  },
                  {
                    path: 'exams/statistics',
                    loadComponent: () => import('./pages/statistics/exam-statistics/exam-statistics.page').then(m => m.ExamStatisticsPage),
                    canActivate: [subjectLicenseGuard]
                  },
                  {
                    path: 'exams/create',
                    loadComponent: () => import('./pages/subjects/subject-teaching/exams/create-exam/create-exam.page').then(m => m.CreateExamPage),
                    canActivate: [subjectLicenseGuard]
                  },
                  {
                    path: 'exams/create/new-package',
                    loadComponent: () => import('./pages/package/details/package-details.page').then(m => m.PackageDetailsPage),
                    canActivate: [subjectLicenseGuard]
                  },
                  {
                    path: 'exams/:learningTaskConfigurationId/statistics',
                    loadComponent: () => import('./pages/statistics/exam-statistics/exam-statistics.page').then(m => m.ExamStatisticsPage),
                    canActivate: [subjectLicenseGuard]
                  },
                  {
                    path: 'exams/:learningTaskConfigurationId',
                    loadComponent: () => import('./pages/subjects/subject-teaching/exams/create-exam/create-exam.page').then(m => m.CreateExamPage),
                    canActivate: [subjectLicenseGuard]
                  },
                  {
                    path: 'exams/:learningTaskConfigurationId/new-package',
                    loadComponent: () => import('./pages/package/details/package-details.page').then(m => m.PackageDetailsPage),
                    canActivate: [subjectLicenseGuard]
                  },
                  {
                    path: 'homelearning',
                    loadComponent: () => import('./pages/subjects/subject-teaching/homelearning/configuration/configuration.page').then(m => m.ConfigurationPage),
                    canActivate: [subjectLicenseGuard]
                  },
                  {
                    path: 'homelearning/students',
                    loadComponent: () => import('./pages/subjects/subject-teaching/homelearning/configuration/configuration.page').then(m => m.ConfigurationPage),
                    data: { studentList: true },
                    canActivate: [subjectLicenseGuard]
                  },
                  {
                    path: 'homelearning/configuration',
                    loadComponent: () => import('./pages/subjects/subject-teaching/homelearning/configuration/configuration.page').then(m => m.ConfigurationPage),
                    canActivate: [subjectLicenseGuard]
                  },
                  {
                    path: 'homelearning/configuration/:studentId',
                    loadComponent: () => import('./pages/subjects/subject-teaching/homelearning/configuration/configuration.page').then(m => m.ConfigurationPage),
                    canActivate: [subjectLicenseGuard]
                  },
                  {
                    path: 'homelearning/statistics',
                    loadComponent: () => import('./pages/statistics/homelearning-statistics/homelearning-statistics.page').then(m => m.HomelearningStatisticsPage),
                    canActivate: [subjectLicenseGuard]
                  },
                  {
                    path: 'homelearning/statistics/:userId',
                    loadComponent: () => import('./pages/statistics/homelearning-statistics/homelearning-statistics.page').then(m => m.HomelearningStatisticsPage),
                    canActivate: [subjectLicenseGuard]
                  },
                  {
                    path: 'students',
                    loadComponent: () => import('./pages/subjects/subject-teaching/students/students.page').then(m => m.StudentsPage)
                  },
                  {
                    path: 'invite',
                    loadComponent: () => import('./pages/subjects/subject-teaching/students/invite-student/invite-student.page').then(m => m.InviteStudentPage),
                    canDeactivate: [inviteStudentDeactivateGuard]
                  },
                  {
                    path: '',
                    redirectTo: 'classworks',
                    pathMatch: 'full'
                  }
                ]
              },
              {
                path: '',
                redirectTo: 'all',
                pathMatch: 'full'
              }
            ]
          },
          {
            path: 'content',
            loadComponent: () => import('./pages/content/list/content-list.page').then(m => m.ContentListPage),
            canActivate: [authGuard]
          },
          {
            path: 'content/recogs/create',
            loadComponent: () => import('./pages/content/details/content-details.page').then(m => m.ContentDetailsPage),
            canActivate: [authGuard]
          },
          {
            path: 'content/recogs/:quizId',
            loadComponent: () => import('./pages/content/details/content-details.page').then(m => m.ContentDetailsPage),
            canActivate: [authGuard]
          },
          {
            path: 'packages',
            loadComponent: () => import('./pages/package/list/package-list.page').then(m => m.PackageListPage),
            canActivate: [authGuard]
          },
          {
            path: 'packages/create',
            loadComponent: () => import('./pages/package/details/package-details.page').then(m => m.PackageDetailsPage),
            canActivate: [authGuard]
          },
          {
            path: 'packages/:quizPackageId',
            loadComponent: () => import('./pages/package/details/package-details.page').then(m => m.PackageDetailsPage),
            canActivate: [authGuard]
          },
          {
            path: 'knowledge-elements/:knowledgeElementId',
            loadComponent: () => import('./pages/knowledge-element/details/knowledge-element-details.page').then(m => m.KnowledgeElementDetailsPage),
            canActivate: [authGuard]
          },
          {
            path: '',
            redirectTo: 'dashboard',
            pathMatch: 'full'
          }
        ]
      }
    ]
  },
  { path: '**', redirectTo: 'select-subject', pathMatch: 'prefix' },
];
